import * as Sentry from '@sentry/vue';
import {
  UserActionEnum,
  OptInEnum,
  validSourceCarriers,
} from '../constants';
import SmsLanding from '../components/SmsLanding.vue';
import SmsSuccess from '../components/SmsSuccess.vue';
import SmsDecline from '../components/SmsDecline.vue';
import SmsError from '../components/SmsError.vue';
import SmsLoader from '../components/SmsLoader.vue';
import SmsUnstopError from '../components/SmsUnstopError.vue';

/**
 * @description Show the success component if the current status is OptIn AND either the page
 * just loaded or the user hit the accept button
 * @param {string} smsStatus The current value of opt-in `@see` OptInEnum
 * @param {number} userAction The current value of the user action `@see` UserActionEnum
 * @param {boolean} smsStatusLoaded Indicates whether or not the opt-in service completed
 * @returns {boolean} true/false
 */
const showSmsSuccess = (smsStatus, userAction, smsStatusLoaded) => {
  const showSmsActions = [UserActionEnum.SMS_PAGE_OPEN, UserActionEnum.SMS_ACCEPT_OPT_IN];
  return smsStatus === OptInEnum.OPT_IN
    && showSmsActions.includes(userAction)
    && smsStatusLoaded;
};

/**
 * @description Show the decline component if the current status is OptOut AND the user
 * hit the decline button
 * @param {string} smsStatus The current value of opt-in `@see` OptInEnum
 * @param {number} userAction The current value of the user action `@see` UserActionEnum
 * @param {boolean} smsStatusLoaded Indicates whether or not the opt-in service completed
 * @returns {boolean} true/false
 */
const showSmsDecline = (smsStatus, userAction, smsStatusLoaded) => (
  smsStatus === OptInEnum.OPT_OUT
  && userAction === UserActionEnum.SMS_DECLINE_OPT_IN
  && smsStatusLoaded
);

/**
 * @description Show the landing component if the page just loaded and the current status
 * is NOT OptIn
 * @param {string} smsStatus The current value of opt-in `@see` OptInEnum
 * @param {number} userAction The current value of the user action `@see` UserActionEnum
 * @param {boolean} smsStatusLoaded Indicates whether or not the opt-in service completed
 * @returns {boolean} true/false
 */
const showSmsLanding = (smsStatus, userAction, smsStatusLoaded) => (
  userAction === UserActionEnum.SMS_PAGE_OPEN
  && smsStatus !== OptInEnum.OPT_IN
  && smsStatusLoaded
);

/**
 * @description Show the unstop error component if the page just loaded and the current status
 * is OptOut
 * @param {string} smsStatus The current value of opt-in `@see` OptInEnum
 * @param {number} userAction The current value of the user action `@see` UserActionEnum
 * @param {boolean} smsStatusLoaded Indicates whether or not the opt-in service completed
 * @param {string } source Source
 * @returns {boolean} true/false
 */
const showSmsUnstopError = (smsStatus, userAction, smsStatusLoaded, source) => (
  userAction === UserActionEnum.SMS_PAGE_OPEN
  && smsStatus === OptInEnum.OPT_OUT
  && validSourceCarriers.includes(source?.toLowerCase?.())
  && smsStatusLoaded
);

/**
 * @description Reverse lookup in object to get key from value
 * @param {object} object The object that has the keys and values
 * @param {string} value The value to get the key from
 * @returns {string} The key from the object that correlates with the value passed
 */
const getKeyByValue = (object, value) => (
  Object.keys(object).find((key) => object[key] === value)
);

/**
 * @description Create an exception to send to Sentry
 * @param {string} message The message we need to send to sentry
 * @param {string} smsStatus The current value of opt-in `@see` OptInEnum
 * @param {number} userAction The current value of the user action `@see` UserActionEnum
 * @param {boolean} smsStatusLoaded Indicates whether the sms status loaded after
 * page load or addOptIn call
 * @param {boolean} isSmsOptInServiceSuccess Indicates whether or not the service
 * loaded successfully
 * @param {object} originalQuery The original query string object from the page load
 */
const sendSentryComponentNotFound = async (
  message,
  smsStatus,
  userAction,
  smsStatusLoaded,
  isSmsOptInServiceSuccess,
  originalQuery,
) => {
  try {
    if (smsStatus) {
      const name = 'COMPONENT_NOT_FOUND';
      const { phoneUuid, programKey } = originalQuery;
      Sentry.setContext(
        name,
        {
          smsStatus,
          userAction: getKeyByValue(UserActionEnum, userAction),
          smsStatusLoaded,
          isSmsOptInServiceSuccess,
          phoneUuid,
          programKey,
        },
      );
    }
    // Don't 'await' in order to prevent 'blocking' the event loop
    // We don't need a response back before carrying on
    Sentry.captureException(message);
  } catch (error) {
    // We don't need any unhandled exceptions
  }
};

/**
 * @description Find the correct component to display
 * @param {string} smsStatus The current value of opt-in `@see` OptInEnum
 * @param {number} userAction The current value of the user action `@see` UserActionEnum
 * @param {boolean} smsStatusLoaded Indicates whether the sms status loaded after
 * page load or addOptIn call
 * @param {boolean} isSmsOptInServiceSuccess Indicates whether or not the service
 * loaded successfully
 * @param {object} originalQuery The original query string object from the page load
 * @param {string } source Source
 * @returns {object} The actual component needed for display
 */
const getComponent = (
  smsStatus,
  userAction,
  smsStatusLoaded,
  isSmsOptInServiceSuccess,
  originalQuery,
  source,
) => {
  // Create a base error message to use to send to Sentry
  let sentryMessage = 'Unable to determine page component for smsOptIn';
  try {
    if (!smsStatusLoaded) {
      return SmsLoader;
    }

    if (!isSmsOptInServiceSuccess) {
      return SmsError;
    }

    if (showSmsSuccess(smsStatus, userAction, smsStatusLoaded)) {
      return SmsSuccess;
    }

    if (showSmsDecline(smsStatus, userAction, smsStatusLoaded)) {
      return SmsDecline;
    }

    if (showSmsUnstopError(smsStatus, userAction, smsStatusLoaded, source)) {
      return SmsUnstopError;
    }

    if (showSmsLanding(smsStatus, userAction, smsStatusLoaded)) {
      return SmsLanding;
    }
  } catch (error) {
    // Append the exception encountered to the error message
    sentryMessage += ` with showSmsLanding exception: ${error.message}`;
  }

  // If we made it here, then we were unable to load the page based off of existing logic,
  // and we will default to the errorPage so send an exception to sentry so it can be researched
  sendSentryComponentNotFound(
    sentryMessage,
    smsStatus,
    userAction,
    smsStatusLoaded,
    isSmsOptInServiceSuccess,
    originalQuery,
  );
  return SmsError;
};

export {
  getComponent,
  getKeyByValue,
  showSmsDecline,
  showSmsLanding,
  showSmsSuccess,
  showSmsUnstopError,
};
